// src/pages/BrowseSubjects.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

export default function BrowseSubjects() {
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [affiliationFilter, setAffiliationFilter] = useState('');
  const [semesterFilter, setSemesterFilter] = useState('');
  const [priceFilter, setPriceFilter] = useState(99);
  const [visibleCount, setVisibleCount] = useState(8);
  const [affiliations, setAffiliations] = useState([]);

  useEffect(() => {
    axios.get('https://backend.scholarkart.in/api/subjects/on-sale')
      .then(res => {
        setSubjects(res.data);
        setFilteredSubjects(res.data);

        // Extract unique affiliations
        const uniqueAffiliations = Array.from(new Set(
          res.data.flatMap(sub => sub.courseIds?.map(c => c.affiliation).filter(Boolean) || [])
        ));
        setAffiliations(uniqueAffiliations);
      })
      .catch(err => console.error('Error fetching subjects:', err));
  }, []);

  useEffect(() => {
    const filtered = subjects.filter(subject => {
      const matchesSearch = subject.subjectName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesAffiliation = !affiliationFilter || subject.courseIds?.some(c => c.affiliation === affiliationFilter);
      const matchesSemester = !semesterFilter || subject.semisterNo === semesterFilter;
      const matchesPrice = subject.price <= priceFilter;
      return matchesSearch && matchesAffiliation && matchesSemester && matchesPrice;
    });
    setFilteredSubjects(filtered);
  }, [searchTerm, affiliationFilter, semesterFilter, priceFilter, subjects]);

  useEffect(() => {
    const handleScroll = () => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 500) {
        setVisibleCount(prev => prev + 4);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 px-6 py-10">
      <h1 className="text-3xl font-bold text-center text-[#264790] mb-10">Browse eLearning Subjects</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Left Panel - Filters */}
        <div className="md:col-span-1 bg-gray-100 dark:bg-gray-800 p-4 rounded-lg space-y-4">
          <input
            type="text"
            placeholder="Search subject name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border p-2 rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
          />

          <select
            value={affiliationFilter}
            onChange={(e) => setAffiliationFilter(e.target.value)}
            className="w-full border p-2 rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
          >
            <option value="">All Affiliations</option>
            {affiliations.map((aff, index) => (
              <option key={index} value={aff}>{aff}</option>
            ))}
          </select>

          <select
            value={semesterFilter}
            onChange={(e) => setSemesterFilter(e.target.value)}
            className="w-full border p-2 rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
          >
            <option value="">All Semesters</option>
            {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
              <option key={n} value={n}>{`Semester ${n}`}</option>
            ))}
          </select>

          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Max Price: ₹{priceFilter}</label>
            <input
              type="range"
              min="0"
              max="199"
              step="1"
              value={priceFilter}
              onChange={(e) => setPriceFilter(Number(e.target.value))}
              className="w-full"
            />
          </div>
        </div>

        {/* Right Panel - Subject Cards */}
        <div className="md:col-span-3 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {filteredSubjects.slice(0, visibleCount).map((subject, idx) => (
            <div key={idx} className="bg-white dark:bg-gray-700 rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition duration-300">
              <img
                src={subject.coverImage}
                alt={subject.subjectName}
                className="w-full h-30 object-cover"
              />
              <div className="p-6">
                <span className="inline-block bg-green-600 text-white text-xs font-semibold px-2 py-1 rounded-full mb-2">
                  ₹{subject.price} one-time
                </span>
                {subject.courseIds?.map((course, index) => (
                  <div key={index} className="text-sm text-gray-500 dark:text-gray-400">
                    {course.courseName} - {course.affiliation}
                  </div>
                ))}
                <h4 className="text-xl font-bold text-[#012f6e] dark:text-white mb-1">
                  {subject.subjectName}
                </h4>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  {subject.subjectOutcome?.slice(0, 100)}...
                </p>
                <Link to={`/subjects_details/${subject._id}`} className="block w-full">
                  <button className="w-10/12 mt-6 bg-[#ff7a00] text-white py-2 rounded-full hover:bg-orange-600 transition">
                    Explore Module
                  </button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}